import React, {useState, useRef, useEffect, forwardRef} from 'react';
import {Phone, Mail, MapPin, Clock} from 'lucide-react';

import {usePages} from '../../context/PagesContext';
import {useNotifications} from '../../components/Notification/UseNotifications';

import styles from './Form.module.scss';
import vk from '../../assets/svg/vk.svg';
import tg from '../../assets/svg/tg.svg';
import yt from '../../assets/svg/youtube.svg';
import instagram from '../../assets/svg/instagram.svg';
import tiktok from '../../assets/svg/tiktok.svg';

const Form = forwardRef<HTMLDivElement>((_props, ref) => {
    const {data} = usePages();
    const {addNotification} = useNotifications();

    const [, setIsSocialMediaOpen] = useState(false);
    const [selectedSocialMedia, setSelectedSocialMedia] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const socialMediaRef = useRef<HTMLDivElement>(null);

    const phone = data?.phone ?? '+7 (999) 999-99-99';
    const email = data?.email ?? 'info@example.com';
    const address = data?.address ?? 'Нижний Новгород, Кремль, 15А';
    const workingDaysWeekdays = data?.workingDaysWeekdays ?? '9:00 - 18:00';
    const workingDaysWeekends = data?.workingDaysWeekends ?? '10:00 - 16:00';
    const vkLink = data?.socialLinks?.vk ?? null;
    const tgLink = data?.socialLinks?.tg ?? null;
    const youtubeLink = data?.socialLinks?.youtube ?? null;
    const instagramLink = data?.socialLinks?.instagram ?? null;
    const tiktokLink = data?.socialLinks?.tiktok ?? null;

    const socialItems = (
        [
            {icon: vk, link: vkLink, name: 'vk'},
            {icon: tg, link: tgLink, name: 'tg'},
            {icon: yt, link: youtubeLink, name: 'youtube'},
            {icon: instagram, link: instagramLink, name: 'instagram'},
            {icon: tiktok, link: tiktokLink, name: 'tiktok'},
        ] as const
    ).filter((s) => Boolean(s.link));

    const formatTelHref = (phoneStr: string) => `tel:${phoneStr.replace(/[^+\d]/g, '')}`;
    const formatEmailHref = (emailStr: string) => `mailto:${emailStr}`;
    const formatMapHref = (addressStr: string) => `https://yandex.ru/maps/?text=${encodeURIComponent(addressStr)}`;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (socialMediaRef.current && !socialMediaRef.current.contains(e.target as Node)) {
                setIsSocialMediaOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [formData, setFormData] = useState({name: '', email: '', phone: '', message: '', socialUsername: ''});
    const [errors, setErrors] = useState({name: '', email: '', phone: '', message: '', socialUsername: ''});
    const [touched, setTouched] = useState({
        name: false,
        email: false,
        phone: false,
        message: false,
        socialUsername: false
    });
    const [hasValue, setHasValue] = useState({
        name: false,
        email: false,
        phone: false,
        message: false,
        socialUsername: false
    });

    const showSuccessNotification = () => {
        addNotification({
            type: 'success',
            title: 'Успешно',
            message: 'Заявка успешно отправлена!',
            duration: 5000
        });
    };

    const showErrorNotification = () => {
        addNotification({
            type: 'error',
            title: 'Ошибка',
            message: 'Ошибка при отправке. Попробуйте еще раз.',
            duration: 5000
        });
    };

    const resetForm = () => {
        setFormData({name: '', email: '', phone: '', message: '', socialUsername: ''});
        setErrors({name: '', email: '', phone: '', message: '', socialUsername: ''});
        setTouched({name: false, email: false, phone: false, message: false, socialUsername: false});
        setHasValue({name: false, email: false, phone: false, message: false, socialUsername: false});
        setSelectedSocialMedia(null);
        setIsSocialMediaOpen(false);
        setIsSubmitting(false);
    };

    const validateName = (v: string) => {
        if (!v) return '';
        if (v.length < 3) return 'Имя должно содержать минимум 3 символа';
        if (!/^[a-zA-Zа-яА-ЯёЁ\s]+$/.test(v)) return 'Имя может содержать только буквы и пробелы';
        return '';
    };

    const validateEmail = (v: string) => (!v ? '' : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Введите корректный email адрес');
    const validatePhone = (v: string) => (!v ? '' : /^(\+7|7|8)?[\s\-]?\(?[489][0-9]{2}\)?[\s\-]?[0-9]{3}[\s\-]?[0-9]{2}[\s\-]?[0-9]{2}$/.test(v.replace(/\s/g, '')) ? '' : 'Введите корректный номер телефона');
    const validateMessage = (v: string) => (v.length >= 10 || !v ? '' : 'Сообщение должно содержать минимум 10 символов');

    const validateSocialUsername = (v: string) => {
        if (!v) return '';
        if (selectedSocialMedia === 'VK') return /^https:\/\/vk\.com\/[a-zA-Z0-9_.]+$/.test(v) ? '' : 'Введите ссылку в формате https://vk.com/username';
        if (selectedSocialMedia === 'Telegram') return /^@[a-zA-Z0-9_]+$/.test(v) ? '' : 'Введите имя пользователя в формате @username';
        return '';
    };

    const validateField = (field: string, value: string) => {
        switch (field) {
            case 'name':
                return validateName(value);
            case 'email':
                return validateEmail(value);
            case 'phone':
                return validatePhone(value);
            case 'message':
                return validateMessage(value);
            case 'socialUsername':
                return validateSocialUsername(value);
            default:
                return '';
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({...prev, [field]: value}));
        if (value && !hasValue[field as keyof typeof hasValue]) setHasValue(prev => ({...prev, [field]: true}));
        if (touched[field as keyof typeof touched]) setErrors(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
    };

    const handlePhoneChange = (value: string) => {
        let cleaned = value.replace(/\D/g, '');
        if (cleaned.startsWith('7') || cleaned.startsWith('8')) cleaned = cleaned.slice(1);
        cleaned = cleaned.slice(0, 10);
        let formatted = '+7 ';
        if (cleaned.length > 0) formatted += '(' + cleaned.slice(0, 3);
        if (cleaned.length > 3) formatted += ') ' + cleaned.slice(3, 6);
        if (cleaned.length > 6) formatted += '-' + cleaned.slice(6, 8);
        if (cleaned.length > 8) formatted += '-' + cleaned.slice(8, 10);
        setFormData(prev => ({...prev, phone: formatted}));
        if (formatted && !hasValue.phone) setHasValue(prev => ({...prev, phone: true}));
        if (touched.phone) setErrors(prev => ({...prev, phone: validatePhone(formatted)}));
    };

    const handleBlur = (field: string) => {
        setTouched(prev => ({...prev, [field]: true}));
        if (hasValue[field as keyof typeof hasValue]) setErrors(prev => ({
            ...prev,
            [field]: validateField(field, formData[field as keyof typeof formData])
        }));
    };
    const submitApplication = async (payload: any) => {
        try {
            const BASE_URL = import.meta.env.VITE_API_BASE_URL;
            const response = await fetch(`${BASE_URL}/api/v1/requests`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({request: payload}),
            });
            return await response.json();
        } catch (err) {
            throw err;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({name: true, email: true, phone: true, message: true, socialUsername: true});
        setHasValue({
            name: !!formData.name,
            email: !!formData.email,
            phone: !!formData.phone,
            message: !!formData.message,
            socialUsername: !!formData.socialUsername,
        });

        const newErrors = {
            name: formData.name ? validateName(formData.name) : 'Имя обязательно для заполнения',
            email: validateEmail(formData.email),
            phone: formData.phone ? validatePhone(formData.phone) : 'Телефон обязателен для заполнения',
            message: formData.message ? validateMessage(formData.message) : 'Сообщение обязательно для заполнения',
            socialUsername: validateSocialUsername(formData.socialUsername),
        };

        setErrors(newErrors);
        const hasRequiredErrors = newErrors.name || newErrors.phone || newErrors.message;
        const hasOptionalErrors = newErrors.email || newErrors.socialUsername;

        if (!hasRequiredErrors && !hasOptionalErrors) {
            setIsSubmitting(true);
            try {
                await submitApplication({
                    name: formData.name,
                    phone: formData.phone,
                    title: 'Заявка с сайта',
                    content: formData.message,
                    email: formData.email || null,
                    telegram: selectedSocialMedia === 'Telegram' ? formData.socialUsername : null,
                    vk: selectedSocialMedia === 'VK' ? formData.socialUsername : null,
                });
                showSuccessNotification();
                resetForm();
            } catch {
                showErrorNotification();
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const contactInfo: Array<{ icon: React.ReactNode; title: string; content: React.ReactNode; href?: string }> = [
        {icon: <Phone size={20}/>, title: 'Телефон', content: phone, href: formatTelHref(phone)},
        {icon: <Mail size={20}/>, title: 'Email', content: email, href: formatEmailHref(email)},
        {icon: <MapPin size={20}/>, title: 'Адрес', content: address, href: formatMapHref(address)},
        {
            icon: <Clock size={20}/>,
            title: 'Часы работы',
            content: <>Пн-Пт: {workingDaysWeekdays}<br/>Сб-Вс: {workingDaysWeekends}</>
        },
    ];

    const handleSocialClick = (platform: string) => {
        const url = platform === 'vk'
            ? vkLink
            : platform === 'tg'
                ? tgLink
                : platform === 'youtube'
                    ? youtubeLink
                    : platform === 'tiktok'
                        ? tiktokLink
                        : instagramLink;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <section className={styles.contactSection} ref={ref}>
            <div className={styles.contactContainer}>
                <div className={styles.contactHeader}>
                    <h2 className={styles.contactTitle}>
                        Присоединяйтесь к нашим <span className={styles.accent}>счастливым клиентам</span>
                    </h2>
                    <p className={styles.description}>
                        Оставьте заявку, и наша команда свяжется с вами для подготовки индивидуального решения.
                    </p>
                </div>

                <div className={styles.contactContent}>
                    <div className={styles.contactInfo}>
                        <h3 className={styles.titleleft}>Свяжитесь с нами</h3>
                        <ul className={styles.contactInfoList}>
                            {contactInfo.map((item, i) => {
                                const isExternal = item.href?.startsWith('http');
                                const content = item.href ? (
                                    <a
                                        className={styles.contactItemLink}
                                        href={item.href}
                                        target={isExternal ? '_blank' : undefined}
                                        rel={isExternal ? 'noopener noreferrer' : undefined}
                                    >
                                        {item.content}
                                    </a>
                                ) : (
                                    <span>{item.content}</span>
                                );

                                return (
                                    <li key={i} className={styles.contactItem}>
                                        <div className={styles.contactIcon}>{item.icon}</div>
                                        <div className={styles.contactDetails}>
                                            <h4 className={styles.contactItemTitle}>{item.title}</h4>
                                            <p className={styles.contactItemContent}>{content}</p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        {socialItems.length > 0 && (
                            <div className={styles.socialSection}>
                                <h4 className={styles.socialTitle}>Мы в соцсетях</h4>
                                <div className={styles.socialIcons}>
                                    {socialItems.map((s) => (
                                        <button
                                            key={s.name}
                                            type="button"
                                            className={`${styles.socialIcon} ${s.name === 'instagram' ? styles.socialIconInstagram : ''}`}
                                            onClick={() => handleSocialClick(s.name)}
                                            title={`Наш ${s.name}`}
                                        >
                                            <img loading={'lazy'} className={styles.socialimg} src={s.icon} alt={s.name}/>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.formSection}>
                        <h3 className={styles.formTitle}>Заполните форму</h3>
                        <form className={styles.contactForm} onSubmit={handleSubmit}>
                            <div className={styles.formRow}>
                                <div className={styles.formField}>
                                    <label htmlFor="name" className={styles.label}>Ваше имя*</label>
                                    <input
                                        id="name"
                                        type="text"
                                        className={`${styles.input} ${errors.name ? styles.error : ''}`}
                                        value={formData.name}
                                        onChange={e => handleInputChange('name', e.target.value)}
                                        onBlur={() => handleBlur('name')}
                                        required
                                    />
                                    {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                                </div>

                                <div className={styles.formField}>
                                    <label htmlFor="phone" className={styles.label}>Телефон*</label>
                                    <input
                                        id="phone"
                                        type="tel"
                                        className={`${styles.input} ${errors.phone ? styles.error : ''}`}
                                        value={formData.phone}
                                        onChange={e => handlePhoneChange(e.target.value)}
                                        onBlur={() => handleBlur('phone')}
                                        placeholder="+7 (XXX) XXX-XX-XX"
                                        required
                                    />
                                    {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                                </div>
                            </div>

                            <div className={styles.formField}>
                                <label htmlFor="email" className={styles.label}>Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    className={`${styles.input} ${errors.email ? styles.error : ''}`}
                                    value={formData.email}
                                    onChange={e => handleInputChange('email', e.target.value)}
                                    onBlur={() => handleBlur('email')}
                                />
                                {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                            </div>

                            <div className={styles.formField}>
                                <label htmlFor="message" className={styles.label}>Сообщение*</label>
                                <textarea
                                    id="message"
                                    rows={4}
                                    className={`${styles.textarea} ${errors.message ? styles.error : ''}`}
                                    value={formData.message}
                                    onChange={e => handleInputChange('message', e.target.value)}
                                    onBlur={() => handleBlur('message')}
                                    required
                                />
                                {errors.message && <span className={styles.errorText}>{errors.message}</span>}
                            </div>

                            {/*
                            А<div className={styles.formField}>
                                <label htmlFor="socialUsername" className={styles.label}>Добавить соцсеть</label>
                                <div className={styles.socialMediaContainer} ref={socialMediaRef}>
                                    <div className={styles.socialMediaHeader}>
                                        <button
                                            type="button"
                                            className={`${styles.input} ${styles.socialMediaButton} ${errors.socialUsername ? styles.error : ''}`}
                                            onClick={() => setIsSocialMediaOpen(!isSocialMediaOpen)}
                                        >
                                            {selectedSocialMedia ? `Ваш ${selectedSocialMedia} аккаунт` : '+ VK / Telegram'}
                                        </button>
                                        {selectedSocialMedia &&
                                            <button type="button" className={styles.clearSocialMedia}
                                                    onClick={resetSocialMedia}><X size={16}/></button>}
                                    </div>

                                    {isSocialMediaOpen && (
                                        <div className={styles.socialMediaOptions}>
                                            <div className={styles.socialMediaOptionsHeader}>
                                                <span>Выберите соцсеть</span>
                                                <button type="button" className={styles.closeOptions}
                                                        onClick={() => setIsSocialMediaOpen(false)}><X size={16}/>
                                                </button>
                                            </div>
                                            {['VK', 'Telegram'].map(platform => (
                                                <div
                                                    key={platform}
                                                    className={styles.socialMediaOption}
                                                    onClick={() => handleSocialMediaSelect(platform)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={e => e.key === 'Enter' && handleSocialMediaSelect(platform)}
                                                >
                                                    {platform}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {selectedSocialMedia && (
                                        <input
                                            id="socialUsername"
                                            type="text"
                                            className={`${styles.input} ${styles.socialMediaInput} ${errors.socialUsername ? styles.error : ''}`}
                                            placeholder={selectedSocialMedia === 'VK' ? 'Введите ссылку https://vk.com/username' : 'Введите @username для Telegram'}
                                            value={formData.socialUsername}
                                            onChange={e => handleInputChange('socialUsername', e.target.value)}
                                            onBlur={() => handleBlur('socialUsername')}
                                        />
                                    )}

                                    {errors.socialUsername &&
                                        <span className={styles.errorText}>{errors.socialUsername}</span>}
                                </div>
                            </div>
*/}
                            <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
});

Form.displayName = 'Form';

export default Form;
